if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.desktopRailMql = window.matchMedia('(min-width: 990px)');
        if (!this.elements.thumbnails) return;

        this.isVerticalThumbnailRail = this.dataset.desktopLayout === 'thumbnail';
        this.thumbnailRail = this.elements.thumbnails.slider;
        this.thumbnailPreviousButton = this.elements.thumbnails.querySelector('[data-topfinel-thumbnail-control="previous"]');
        this.thumbnailNextButton = this.elements.thumbnails.querySelector('[data-topfinel-thumbnail-control="next"]');

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        this.thumbnailPreviousButton?.addEventListener('click', () => this.scrollThumbnailRail(-1));
        this.thumbnailNextButton?.addEventListener('click', () => this.scrollThumbnailRail(1));
        this.thumbnailRail?.addEventListener('scroll', this.updateThumbnailScrollState.bind(this), { passive: true });
        this.thumbnailVisibilityObserver = new MutationObserver(() => this.updateThumbnailScrollState());
        this.thumbnailVisibilityObserver.observe(this.thumbnailRail, { attributes: true, childList: true, subtree: true });
        this.thumbnailControlStateObserver = new MutationObserver(() => this.updateThumbnailScrollState());
        this.thumbnailControlStateObserver.observe(this.elements.thumbnails, {
          attributes: true,
          attributeFilter: ['disabled'],
          subtree: true,
        });
        this.handleThumbnailResize = debounce(this.updateThumbnailScrollState.bind(this), 150);
        window.addEventListener('resize', this.handleThumbnailResize);
        this.mql.addEventListener('change', this.handleThumbnailResize);
        this.desktopRailMql.addEventListener('change', this.handleThumbnailResize);
        this.updateThumbnailScrollState();
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
        this.updateThumbnailScrollState();
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);

        const isVerticalThumbnailRail = this.desktopRailMql.matches && this.dataset.desktopLayout === 'thumbnail';
        if (isVerticalThumbnailRail) {
          const thumbnailRail = this.elements.thumbnails.slider;
          const thumbnailTop = thumbnail.offsetTop;
          const thumbnailBottom = thumbnailTop + thumbnail.offsetHeight;
          const visibleTop = thumbnailRail.scrollTop;
          const visibleBottom = visibleTop + thumbnailRail.clientHeight;

          if (thumbnailTop < visibleTop) {
            thumbnailRail.scrollTo({ top: thumbnailTop, behavior: 'smooth' });
          } else if (thumbnailBottom > visibleBottom) {
            thumbnailRail.scrollTo({ top: thumbnailBottom - thumbnailRail.clientHeight, behavior: 'smooth' });
          }
          return;
        }

        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
        this.updateThumbnailScrollState();
      }

      scrollThumbnailRail(direction) {
        if (!this.isVerticalThumbnailRail || !this.desktopRailMql.matches || !this.thumbnailRail) return;

        const thumbnail = this.thumbnailRail.querySelector('.thumbnail-list__item:not([hidden])');
        if (!thumbnail) return;
        const gap = parseFloat(getComputedStyle(this.thumbnailRail).rowGap || getComputedStyle(this.thumbnailRail).gap) || 0;
        const step = (thumbnail.offsetHeight + gap) * 2;
        this.thumbnailRail.scrollBy({ top: direction * step, behavior: 'smooth' });
      }

      updateThumbnailScrollState() {
        if (!this.thumbnailRail || !this.thumbnailPreviousButton || !this.thumbnailNextButton) return;

        const isDesktopVerticalRail = this.isVerticalThumbnailRail && this.desktopRailMql.matches;
        const canScroll = isDesktopVerticalRail && this.thumbnailRail.scrollHeight > this.thumbnailRail.clientHeight + 1;
        const atTop = this.thumbnailRail.scrollTop <= 1;
        const atBottom = this.thumbnailRail.scrollTop + this.thumbnailRail.clientHeight >= this.thumbnailRail.scrollHeight - 1;

        this.elements.thumbnails.classList.toggle('has-scrollable-thumbnails', canScroll);
        this.thumbnailPreviousButton.classList.toggle('is-scroll-boundary', !canScroll || atTop);
        this.thumbnailNextButton.classList.toggle('is-scroll-boundary', !canScroll || atBottom);
        const previousDisabled = !canScroll || atTop;
        const nextDisabled = !canScroll || atBottom;
        if (this.thumbnailPreviousButton.disabled !== previousDisabled) {
          this.thumbnailPreviousButton.disabled = previousDisabled;
        }
        if (this.thumbnailNextButton.disabled !== nextDisabled) {
          this.thumbnailNextButton.disabled = nextDisabled;
        }
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
